import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * ProtectedRoute
 *
 *   <ProtectedRoute anyPermission={['management.view']}> … </ProtectedRoute>
 *
 * Reglas:
 *   - `anyPermission` (preferido): array de permisos; el usuario pasa si tiene
 *     al menos uno. Admin tiene todos (incluidos implícitamente desde el
 *     backend). Un rol custom pasa si su documento `roles` en la DB contiene
 *     alguno de esos permisos.
 *   - Si no se pasa `anyPermission` (ni array vacío ni ausente), basta con
 *     estar autenticado (equivalente al `ProtectedRoute` sin restricciones).
 *   - `allowedRoles` queda como legacy opcional (ya no se usa en App.js).
 */
export function ProtectedRoute({ children, anyPermission, allowedRoles = [] }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2B4C3B]"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Permisos reales del usuario (vienen frescos del backend en /auth/me y /login)
  const perms = Array.isArray(user.permissions) ? user.permissions : [];

  if (Array.isArray(anyPermission) && anyPermission.length > 0) {
    const ok = anyPermission.some((p) => perms.includes(p));
    if (!ok) return <Navigate to="/dashboard" replace />;
    return children;
  }

  // Legacy: lista cerrada de roles por nombre (no la usamos ya en App.js,
  // pero la mantenemos para no romper si alguien la invoca).
  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}
